// Controls (Dota-like, no attack-move key), ported from prototypes/match/js/input.js. Every call this
// module makes on the game is game.apply(playerId, cmd) — nothing else. The pure decision functions below
// (decideRightClick/decideCast) mirror input.js's old targetAt()/rightClick() branching exactly and are
// unit-testable with plain fake Hero-like objects, no DOM or THREE needed.
//   RMB ground → move · RMB enemy, enemy totem, enemy summon or monster → go and hit it · RMB ally → go to it and stay close
//   T → the team's relic (both relic actives are instant).
//   Q W E R → skills. instant: fires at once · direction/point/ally: aim, LMB casts · charge: hold LMB, release fires
//   RMB/Esc cancels aiming. Key codes, not characters, so the Russian layout works the same.
import * as THREE from 'three';
import { mk, flat, forget } from './engrave.js';
import { SKILLS } from '../core/skills.js';
import { ARTIFACTS, artifactReady } from '../core/artifacts.js';
import { RELICS } from '../core/relics.js';
import type { ArtifactSlotKey, CastKey, CastTarget, Command, SkillKey } from '../core/commands.js';
import { MONSTERS } from '../core/monsters.js';
import { SUMMONS } from '../core/summons.js';
import type { Vec2 } from '../core/events.js';
import type { Team } from '../core/layout.js';
import type { GameView, ViewHero, ViewMonster, ViewSummon, ViewTotem } from './gameView.js';
import { MONSTER_LOOK } from './monsterView.js';
import { SUMMON_LOOK } from './summonView.js';
import { t } from './i18n/index.js';
import { artifactName, relicName, skillName } from './i18n/names.js';

/** Anything under the cursor a click can mean: a hero, a neutral monster or a summoned creature. */
export type Unit = ViewHero | ViewMonster | ViewSummon;
// By kind, not by a field: online heroes also carry maxHp, which made every hero look like a monster.
export const isMonsterUnit = (u: Unit): u is ViewMonster => Object.hasOwn(MONSTERS, u.kind);
export const isSummonUnit = (u: Unit): u is ViewSummon => Object.hasOwn(SUMMONS, u.kind);
/** Ring radius under a unit, for the hover/focus/hit marks. */
export const ringOf = (u: Unit) => (isMonsterUnit(u) ? MONSTER_LOOK[u.kind].ring : isSummonUnit(u) ? SUMMON_LOOK[u.kind].ring : 1);

const isArtifactKey = (key: CastKey): key is ArtifactSlotKey => key === 'E' || key === 'R';

/* What a ground right-click (or a right-click on a unit) turns into. `hit` must already be filtered by
   visibleTo (see unitAt below) — the core rejects an invisible target anyway, but a client should never
   even try. */
export function decideRightClick(hit: Unit | null, self: ViewHero, ground: Vec2 | null): Command | null {
  if (hit && isMonsterUnit(hit)) return { type: 'attack', target: { monster: hit.id } };
  if (hit && isSummonUnit(hit)) return hit.team !== self.team ? { type: 'attack', target: { summon: hit.id } } : ground ? { type: 'move', x: ground.x, z: ground.z } : null;
  if (hit && hit.team !== self.team) return { type: 'attack', target: { hero: hit.id } };
  if (hit && hit !== self) return { type: 'follow', target: hit.id };
  if (ground) return { type: 'move', x: ground.x, z: ground.z };
  return null;
}

/* Whether a right-click goes to the enemy totem under the cursor: only an enemy hero or a monster there too
   takes the click from it — the own hero or an ally standing by it never does. */
export function totemTakesClick(hit: Unit | null, self: ViewHero, totemUnderCursor: boolean): boolean {
  return totemUnderCursor && (!hit || (!isMonsterUnit(hit) && hit.team === self.team));
}

/* What LMB would target right now for the aimed skill, mirroring input.js's targetAt(). A Q/W skill aims at
   a monster by its position; 'enemy' targeting (Клеймо) names the unit itself — an enemy hero's id or
   `{ monster }` — and never falls back to the ground, which core would refuse. */
export function decideCastTarget(targeting: string, hit: Unit | null, self: ViewHero, ground: Vec2 | null): CastTarget {
  if (targeting === 'enemy') {
    if (!hit) return null;
    if (isMonsterUnit(hit)) return { monster: hit.id };
    if (isSummonUnit(hit)) return hit.team !== self.team ? { summon: hit.id } : null;
    return hit.team !== self.team ? hit.id : null;
  }
  if (hit && (isMonsterUnit(hit) || isSummonUnit(hit))) {
    if (targeting === 'ally') return self.id;
    return targeting === 'point' ? ground : { x: hit.pos.x, z: hit.pos.z };
  }
  if (targeting === 'ally') return hit && hit.team === self.team ? hit.id : self.id;
  if (hit && hit.team !== self.team && targeting !== 'point') return hit.id;
  return ground;
}

/* Why an 'enemy' cast (Клеймо) at this unit would be refused, or null when it will land — the same rules
   core's castArtifact applies: a live enemy hero, or a live monster not leashing home, within `range` of
   the caster's centre plus the monster's bulk. A refused click keeps the aim up and toasts this instead. */
export function enemyCastProblem(hit: Unit | null, self: ViewHero, range: number): string | null {
  if (!hit || !hit.alive || (!isMonsterUnit(hit) && hit.team === self.team)) return t('cast.pickFoe');
  if (isMonsterUnit(hit) && hit.state === 'return') return t('cast.monsterReturning');
  const reach = range + (isMonsterUnit(hit) ? MONSTERS[hit.kind].radius : isSummonUnit(hit) ? SUMMONS[hit.kind].radius : 0);
  if (Math.hypot(hit.pos.x - self.pos.x, hit.pos.z - self.pos.z) > reach) return t('cast.tooFar', { range });
  return null;
}

export function createInput({ stageEl, canvas, camera, game, scene, toast, stalls, onOpenShop, consumePress }: {
  stageEl: HTMLElement; canvas: HTMLCanvasElement; camera: THREE.Camera; game: GameView; scene: THREE.Scene; toast: (s: string) => void;
  /** The player's own base's stall (mapView.ts) — the one narrow raycast target for "click opens the
      shop" and its hover highlight. Only the player's own team's stall is ever tested: the enemy one is
      irrelevant to a click, being unusable and usually unreachable besides. */
  stalls: Record<Team, { hit: THREE.Object3D; hoverRing: THREE.Object3D }>;
  onOpenShop: () => void;
  /** Asked first on every press on the world: true means the press was used up by something else (the
      open shop closing), so it must not also become an order. */
  consumePress?: () => boolean;
}) {
  const player = game.player as ViewHero;
  const skills: Record<string, any> = (SKILLS as any)[player.kind] || {};
  const myStall = stalls[player.team];
  const ray = new THREE.Raycaster(), ground = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  const mouse = { x: 0, y: 0, inside: false };
  // `chargePress`: a charge cast's LMB press is open. Tracked from the press, not from `player.state`:
  // online apply() only sends the cast, and the own hero's state reads 'charging' a frame or more later
  // (or flickers on a misprediction). aim/release are core no-ops unless actually charging, so sending
  // them for a press whose charge never started (or was interrupted) is harmless.
  let targeting: CastKey | null = null, chargePress = false;

  /* Q/W come from the hero's fixed SKILLS table; E/R come from whatever the shop put in that slot — same
     `{name, targeting, range?, r?}` shape either way, so leftDown()/onKeyDown()/update() below never need
     to know which kind of cast they're driving. `null` for an empty slot OR a passive one (docs' flat,
     one-level artefacts, 2026-09-23): a passive has no button and no targeting shape to speak of, same as
     `skills[key]` being undefined already meant "nothing to aim here". */
  function describe(key: CastKey): any | null {
    if (key === 'T') return null;  // never aimed: see onKeyDown's own T branch
    if (!isArtifactKey(key)) return skills[key] ? { ...skills[key], name: skillName(player.kind, key as SkillKey) } : null;
    const slot = player.artifacts[key];
    if (!slot) return null;
    const def = ARTIFACTS[slot.line] as any;
    return def.kind === 'active' ? { name: artifactName(slot.line), targeting: def.targeting, range: def.range, r: def.r } : null;
  }

  const aimRing = flat(mk(new THREE.RingGeometry(0.97, 1, 96), 0xf2d46b, { fx: true, cast: false }), 0.28);
  const aimLine = flat(mk(new THREE.PlaneGeometry(1, 1), 0xf2d46b, { fx: true, cast: false }), 0.29);
  const aimSpot = flat(mk(new THREE.RingGeometry(0.9, 1, 48), 0xf2d46b, { fx: true, cast: false }), 0.29);
  const moveMark = flat(mk(new THREE.RingGeometry(0.4, 0.55, 32), 0xb8e07a, { fx: true, cast: false }), 0.3);
  const hitMark = flat(mk(new THREE.RingGeometry(0.95, 1.12, 40), 0xe0604a, { fx: true, cast: false }), 0.31);
  scene.add(aimRing, aimLine, aimSpot, moveMark, hitMark);
  [aimRing, aimLine, aimSpot, moveMark, hitMark].forEach(m => { m.visible = false; });
  const marks = { move: -9, hit: -9, hitTarget: null as Unit | null, at: new THREE.Vector3() };
  // The target of the standing attack order, as far as this client knows: the wire carries no orders, so
  // it is the last attack clicked, dropped when core would drop the order (see focusHolds).
  const focus = { unit: null as Unit | null, at: -9 };

  function groundAt(px: number, py: number): THREE.Vector3 | null {
    const r = stageEl.getBoundingClientRect();
    ray.setFromCamera(new THREE.Vector2((px / r.width) * 2 - 1, -(py / r.height) * 2 + 1), camera);
    const hit = new THREE.Vector3();
    return ray.ray.intersectPlane(ground, hit) ? hit : null;
  }

  /* The unit under the cursor: nearest projected body within a generous screen radius, visible only.
     A monster is sampled along its whole body (2–4.7 m nose to tail) and gets a wider radius; a hero
     standing in front of it still wins whenever the cursor is nearer the hero. */
  function unitAt(px: number, py: number, monsters: readonly ViewMonster[] = game.monsters, summons: readonly ViewSummon[] = game.summons): Unit | null {
    const r = stageEl.getBoundingClientRect();
    let best: Unit | null = null, bestD = Infinity;
    const probe = (u: Unit, x: number, y: number, z: number, radius: number) => {
      const v = new THREE.Vector3(x, y, z).project(camera);
      const d = Math.hypot((v.x * 0.5 + 0.5) * r.width - px, (-v.y * 0.5 + 0.5) * r.height - py);
      if (d < radius && d < bestD) { bestD = d; best = u; }
    };
    for (const h of game.heroes) {
      if (!h.alive || !game.visibleTo(player.team, h)) continue;
      for (const y of [0.4, 1.2, 2.0]) probe(h, h.pos.x, y, h.pos.z, 42);
    }
    for (const m of monsters) {
      const half = MONSTER_LOOK[m.kind].half, fx = Math.sin(m.yaw), fz = Math.cos(m.yaw);
      for (const k of [-1, -0.5, 0, 0.5, 1]) for (const y of [0.6, 1.5]) probe(m, m.pos.x + fx * half * k, y, m.pos.z + fz * half * k, 50);
    }
    for (const s of summons) for (const y of SUMMON_LOOK[s.kind].pickY) probe(s, s.pos.x, y, s.pos.z, 34);
    return best;
  }
  /* An enemy totem under the cursor (same screen-radius probe as a hero); totemTakesClick decides whether a
     unit under the cursor too wins the click instead. */
  function enemyTotemAt(px: number, py: number): ViewTotem | null {
    const r = stageEl.getBoundingClientRect();
    let best: ViewTotem | null = null, bestD = 36;
    for (const t of game.totems) {
      if (t.team === player.team) continue;
      for (const y of [0.4, 1.2]) {
        const v = new THREE.Vector3(t.pos.x, y, t.pos.z).project(camera);
        const d = Math.hypot((v.x * 0.5 + 0.5) * r.width - px, (-v.y * 0.5 + 0.5) * r.height - py);
        if (d < bestD) { bestD = d; best = t; }
      }
    }
    return best;
  }
  // `monsters` is the frame's one read of game.monsters (offline it builds a fresh array per read).
  const liveUnit = (u: Unit, monsters: readonly ViewMonster[]) => (isMonsterUnit(u) ? monsters.includes(u) : isSummonUnit(u) ? game.summons.includes(u) : u.alive);
  /* Mirrors game.ts's updateHero: an attack order ends with the attacker's death, the target's, or the
     target leaving the attacker's team's sight — the last two only checked while the attacker is free
     (not stunned, pulled or mid-skill: updateHero returns before its order check then). A monster the
     view no longer lists is dead or out of sight, so it too waits for a free attacker. A clock that
     went backward is a new match (see marks below). */
  function focusHolds(u: Unit, monsters: readonly ViewMonster[], free: boolean) {
    if (!player.alive || focus.at > game.t) return false;
    if (isMonsterUnit(u)) return !free || monsters.includes(u);
    if (isSummonUnit(u)) return !free || game.summons.includes(u);
    return u.alive && (!free || game.visibleTo(player.team, u));
  }

  /* Narrow on purpose: only the player's OWN stall (see myStall above), never tested unless the hero is
     actually standing in that base — the whole reason it's raycast-gated rather than a screen-space click
     zone like unitAt's is that a stall sitting far offscreen must never register a click meant for the
     game world behind where it WOULD be if the camera panned there. */
  function stallAt(px: number, py: number): boolean {
    const r = stageEl.getBoundingClientRect();
    ray.setFromCamera(new THREE.Vector2((px / r.width) * 2 - 1, -(py / r.height) * 2 + 1), camera);
    return ray.intersectObject(myStall.hit, true).length > 0;
  }

  const local = (e: PointerEvent) => { const r = stageEl.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };

  function rightClick(px: number, py: number) {
    if (!player.alive) return;
    const u = unitAt(px, py), g = groundAt(px, py);
    const totem = enemyTotemAt(px, py);
    if (totem && totemTakesClick(u, player, true)) {
      game.apply(player.id, { type: 'attack', target: { totem: totem.id } });
      if (!game.statusOf(player).taunted) { focus.unit = null; focus.at = game.t; }
      return;
    }
    const cmd = decideRightClick(u, player, g);
    if (!cmd) return;
    game.apply(player.id, cmd);
    // Taunted: core ignores the order and keeps the attack on the taunter (update() tracks that).
    if (!game.statusOf(player).taunted) { focus.unit = cmd.type === 'attack' ? u : null; focus.at = game.t; }
    if (cmd.type === 'attack') { marks.hit = game.t; marks.hitTarget = u; }
    else if (cmd.type === 'follow') { marks.move = game.t; marks.at.copy((u as ViewHero).pos); }
    else { marks.move = game.t; marks.at.copy(g as THREE.Vector3); }
  }

  function leftDown(px: number, py: number) {
    if (!targeting) return;
    const key = targeting, sk = describe(key);
    if (!sk) { targeting = null; return; }  // the slot emptied (sold) while aiming it
    const u = unitAt(px, py), g = groundAt(px, py);
    if (sk.targeting === 'enemy') {
      const problem = enemyCastProblem(u, player, sk.range);
      if (problem) { toast(t('toast.named', { name: sk.name, text: problem })); return; }
    }
    const target = decideCastTarget(sk.targeting, u, player, g);
    if (target === null) return;
    // canCast()/canCastArtifact() predicts the real cast's own success gate exactly (the only other thing
    // it checks — a non-null target for a targeting kind that needs one — is already true at this point);
    // reading it here is a query, not a mutation, same as the visibleTo() read already used above.
    const willCast = isArtifactKey(key) ? game.canCastArtifact(player, key) : key !== 'T' && game.canCast(player, key);
    if (sk.targeting === 'charge') {
      game.apply(player.id, { type: 'cast', key, target });
      chargePress = true;  // even if willCast guesses no: aim/release are no-ops for a hero not charging
      targeting = null;
      return;
    }
    game.apply(player.id, { type: 'cast', key, target });
    // The prototype's marker required both a resolved enemy hero AND a successful cast (input.js:83).
    // `target` is only ever a hero id here for a non-'ally' targeting kind when that hero is an enemy
    // (decideCastTarget's own contract), so this is exactly that same pair of conditions.
    if (willCast && sk.targeting !== 'ally' && typeof target === 'number') {
      const enemy = game.heroes[target];
      if (enemy) { marks.hit = game.t; marks.hitTarget = enemy; }
    } else if (willCast && u && (isMonsterUnit(u) || (isSummonUnit(u) && u.team !== player.team)) && (sk.targeting === 'direction' || sk.targeting === 'charge' || sk.targeting === 'enemy')) {
      marks.hit = game.t; marks.hitTarget = u;
    }
    targeting = null;
  }

  // Named, not inline, so destroy() below can remove exactly these — stageEl/canvas/window are all reused
  // across matches in the same page session (the online lobby's "play again" loop never reloads), so a
  // fresh createInput() per match without this would double, triple, ... every handler.
  const onContextMenu = (e: Event) => e.preventDefault();
  const onPointerDown = (e: PointerEvent) => {
    if (consumePress?.()) return;
    const { x, y } = local(e);
    if (e.button === 2) {
      if (targeting) targeting = null;
      else if (chargePress && player.state === 'charging') return;  // mid-charge RMB does nothing, as before
      // Not (yet) charging locally: the order goes through, but the press stays open — releasing here
      // would fire a minimum shot if the server is already charging; LMB up still releases.
      else rightClick(x, y);
    } else if (e.button === 0) {
      // Clicking the stall while aiming a skill still casts (RMB/Esc is the one cancel gesture, matching
      // every other aim) — the stall only intercepts a PLAIN click, same as leftDown() being a no-op then.
      if (!targeting && game.you.inBase && stallAt(x, y)) { onOpenShop(); return; }
      // Not aiming a skill: LMB orders exactly like RMB (attack an enemy or monster, walk, follow an ally).
      if (!targeting) { if (!(chargePress && player.state === 'charging')) rightClick(x, y); return; }
      leftDown(x, y);
    }
  };
  function releaseCharge() {
    if (!chargePress) return;
    chargePress = false;
    // Online aim is throttled (RemoteGameView.apply), so this last one may be dropped if one went out <0.1 s ago.
    const g = mouse.inside ? groundAt(mouse.x, mouse.y) : null;
    if (g) game.apply(player.id, { type: 'aim', x: g.x, z: g.z });
    game.apply(player.id, { type: 'release' });
  }
  // A browser fires pointerup only for the LAST button released: LMB let go while RMB is still held shows up
  // as a pointermove (or a pointerup of another button) whose `buttons` no longer has the left bit.
  const lmbUp = (e: PointerEvent) => (e.buttons & 1) === 0;
  const onPointerUp = (e: PointerEvent) => { if (e.button === 0 || lmbUp(e)) releaseCharge(); };
  // A pointerup outside a focused window never arrives: losing focus or the tab ends the press instead.
  const onBlur = () => releaseCharge();
  const onVisibility = () => { if (document.visibilityState === 'hidden') releaseCharge(); };
  const onPointerMove = (e: PointerEvent) => {
    Object.assign(mouse, local(e), { inside: true });
    if (chargePress && lmbUp(e)) releaseCharge();
  };
  const onPointerLeave = () => { mouse.inside = false; };

  stageEl.addEventListener('contextmenu', onContextMenu);
  canvas.addEventListener('pointerdown', onPointerDown);
  window.addEventListener('pointerup', onPointerUp);
  window.addEventListener('blur', onBlur);
  const doc = typeof document !== 'undefined' && typeof document.addEventListener === 'function' ? document : null;  // absent in DOM-less tests
  doc?.addEventListener('visibilitychange', onVisibility);
  canvas.addEventListener('pointermove', onPointerMove);
  canvas.addEventListener('pointerleave', onPointerLeave);

  const KEYS: Record<string, CastKey> = { KeyQ: 'Q', KeyW: 'W', KeyE: 'E', KeyR: 'R', KeyT: 'T' };
  /* T: the team's relic — nothing to aim, so it fires at once or says why not. */
  function pressRelic() {
    const line = game.relics[player.team];
    if (!line) { toast(t('toast.noRelic')); return; }
    const def = RELICS[line];
    if (def.kind === 'passive') { toast(t('toast.relicPassive', { name: relicName(line) })); return; }
    const cd = game.you.relicCd ?? 0;
    if (cd > 0) { toast(t('toast.cooling', { name: relicName(line), s: Math.ceil(cd) })); return; }
    if (game.canCastRelic(player)) game.apply(player.id, { type: 'cast', key: 'T' });
  }
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.code === 'Escape') { targeting = null; return; }
    const key = KEYS[e.code];
    if (!key || e.repeat) return;
    e.preventDefault();
    if (!player.alive) return;
    if (key === 'T') { pressRelic(); return; }
    const artifact = isArtifactKey(key);
    const sk = describe(key);
    if (!sk) {
      const held = artifact ? player.artifacts[key] : null;
      if (held) toast(t('toast.artifactPassive', { name: artifactName(held.line) }));
      // E/R's empty-slot message points at the shop instead of at a skill purchase — same slot, different
      // way to fill it (docs' own "shop that only works inside your own base").
      else toast(t(artifact ? 'toast.emptyArtifactSlot' : 'toast.emptySkillSlot', { key }));
      return;
    }
    const cd = artifact ? game.artifactCdFor(player, key) : player.cd[key as SkillKey];
    const line = artifact ? player.artifacts[key]?.line : undefined;
    if (line === 'bloodvial') {
      if (!artifactReady({ line, cd })) { toast(t('toast.vialEmpty', { name: sk.name })); return; }
    } else if (cd > 0) { toast(t('toast.cooling', { name: sk.name, s: Math.ceil(cd) })); return; }
    if (!(artifact ? game.canCastArtifact(player, key) : game.canCast(player, key as SkillKey))) return;
    if (sk.targeting === 'instant') game.apply(player.id, { type: 'cast', key });
    else targeting = targeting === key ? null : key;
  };
  window.addEventListener('keydown', onKeyDown);

  let hovered: Unit | null = null;

  function update(monsters: readonly ViewMonster[] = game.monsters) {
    const g = mouse.inside ? groundAt(mouse.x, mouse.y) : null;
    if ((chargePress || player.state === 'channel') && g) game.apply(player.id, { type: 'aim', x: g.x, z: g.z });
    const sk = targeting && describe(targeting);
    const hover = mouse.inside ? unitAt(mouse.x, mouse.y, monsters) : null;
    hovered = hover;
    const inBase = game.you.inBase;
    const stallHover = inBase && mouse.inside && stallAt(mouse.x, mouse.y);
    myStall.hoverRing.visible = stallHover;
    stageEl.dataset.cursor = targeting ? 'target' : hover && (isMonsterUnit(hover) || hover.team !== player.team) ? 'attack' : hover && hover !== player ? 'ally' : stallHover ? 'shop' : '';

    aimRing.visible = aimLine.visible = aimSpot.visible = false;
    if (sk && player.alive) {
      const range = Array.isArray(sk.range) ? sk.range[1] : sk.range;
      aimRing.visible = true;
      aimRing.position.set(player.pos.x, 0.28, player.pos.z);
      aimRing.scale.setScalar(range);
      const g2 = mouse.inside ? groundAt(mouse.x, mouse.y) : null;
      const u2 = mouse.inside ? unitAt(mouse.x, mouse.y, monsters) : null;
      const target = mouse.inside ? decideCastTarget(sk.targeting, u2, player, g2) : null;
      const tp = target && typeof target === 'object' ? ('monster' in target || 'summon' in target ? null : new THREE.Vector3(target.x, 0, target.z))
        : target !== null && target !== undefined ? game.heroes[target]!.pos : null;
      if (tp && (sk.targeting === 'direction' || sk.targeting === 'charge')) {
        const sign = sk.away ? -1 : 1;
        const dx = (tp.x - player.pos.x) * sign, dz = (tp.z - player.pos.z) * sign;
        const len = sk.away || sk.targeting === 'charge' ? range : Math.min(range, Math.hypot(dx, dz));
        const a = Math.atan2(dx, dz);
        aimLine.visible = true;
        aimLine.scale.set(sk.width || 0.9, len, 1);
        aimLine.rotation.set(-Math.PI / 2, 0, a + Math.PI);
        aimLine.position.set(player.pos.x + Math.sin(a) * len / 2, 0.29, player.pos.z + Math.cos(a) * len / 2);
      } else if (tp && sk.targeting === 'point') {
        const dx = tp.x - player.pos.x, dz = tp.z - player.pos.z, d = Math.hypot(dx, dz), k = d > range ? range / d : 1;
        aimSpot.visible = true;
        aimSpot.position.set(player.pos.x + dx * k, 0.29, player.pos.z + dz * k);
        aimSpot.scale.setScalar(sk.r);
      } else if (tp && sk.targeting === 'ally') {
        aimSpot.visible = true;
        aimSpot.position.set(tp.x, 0.29, tp.z);
        aimSpot.scale.setScalar(1.1);
      }
    }
    // ROOT CAUSE of the giant-ring report (a huge green/red circle sweeping the map, shrinking toward
    // normal over the next several minutes): `marks.move`/`marks.hit` record `game.t` at click time, and
    // `game.t` is core's OWN `Game.t` offline — which `Game.reset()` zeros six seconds after a winner is
    // set (game.ts's own step(): `if (this.winnerT > 6) this.reset()`), the engine's built-in same-tab
    // "next match" flow. That reset never recreates this module (main.ts keeps the same running
    // createInput() across it, same as it keeps the same Game), so a mark set late in one match (say
    // `game.t = 395`) survives into the next one, where `game.t` starts back at 0: `mAge` comes out around
    // -395, `1.2 - mAge*1.6` is a scale in the hundreds, and it only shrinks back toward normal once the
    // NEW match's clock climbs back past where the old mark was — exactly "a giant ring, closing inward,
    // minutes into a session, after a previous match" every symptom in the report. `game.t` cannot
    // legitimately go backward otherwise (core's own step() only ever adds to it; RemoteGameView.tick()
    // online is the same), so a negative age is unambiguously this, not ordinary timing noise — the fix is
    // to recognize a mark from before a reset and drop it, not to keep drawing whatever the stale math
    // produces.
    const status = game.statusOf(player);
    // A taunt forces the order onto the taunter; online the wire never says who that is, so no focus.
    if (status.taunted) { focus.unit = game.tetherSource(player); focus.at = game.t; }
    else if (focus.unit) {
      const free = player.state === 'normal' && game.t >= player.stunUntil && !status.pulled;
      if (!focusHolds(focus.unit, monsters, free)) focus.unit = null;
    }
    if (marks.move > game.t) marks.move = -9;
    if (marks.hit > game.t) { marks.hit = -9; marks.hitTarget = null; }
    const mAge = game.t - marks.move;
    moveMark.visible = mAge < 0.45;
    if (moveMark.visible) { moveMark.position.set(marks.at.x, 0.3, marks.at.z); moveMark.scale.setScalar(1.2 - mAge * 1.6); }
    const hAge = game.t - marks.hit, tg = marks.hitTarget;
    hitMark.visible = !!tg && liveUnit(tg, monsters) && hAge < 0.6;
    if (hitMark.visible) {
      const size = ringOf(tg!);
      hitMark.position.set(tg!.pos.x, 0.31, tg!.pos.z);
      hitMark.scale.setScalar((1.3 - hAge * 0.5) * size);
    }
  }

  function destroy() {
    stageEl.removeEventListener('contextmenu', onContextMenu);
    canvas.removeEventListener('pointerdown', onPointerDown);
    window.removeEventListener('pointerup', onPointerUp);
    window.removeEventListener('blur', onBlur);
    doc?.removeEventListener('visibilitychange', onVisibility);
    canvas.removeEventListener('pointermove', onPointerMove);
    canvas.removeEventListener('pointerleave', onPointerLeave);
    window.removeEventListener('keydown', onKeyDown);
    for (const m of [aimRing, aimLine, aimSpot, moveMark, hitMark]) { scene.remove(m); forget(m); }
    myStall.hoverRing.visible = false;  // mapView's meshes outlive one match (built once in main.ts)
  }

  return {
    update, destroy,
    get targeting() { return targeting; },
    get hovered() { return hovered; },
    get focus() { return focus.unit; },
  };
}
